import { useState, useMemo } from 'react';
import { ContentCopy, Check } from '@mui/icons-material';
import DepartureTopStrip from '../components/ui/DepartureTopStrip';
import PhoneTopStrip from '../components/ui/PhoneTopStrip';
import useRenderMode from '../hooks/useRenderMode';

const portalUrl =
  import.meta.env.VITE_FAME_PORTAL_URL || 'https://truhr.mydiyosfame.com';

const buildFameCredentials = () => {
  const employeeId = import.meta.env.VITE_FAME_EMPLOYEE_ID ?? '';
  const username = import.meta.env.VITE_FAME_USERNAME ?? '';
  const password = import.meta.env.VITE_FAME_PASSWORD ?? '';
  return [
    { label: 'Employee ID', value: employeeId, key: 'empid' },
    { label: 'Username', value: username, key: 'username' },
    { label: 'Password', value: password, key: 'password', sensitive: true },
  ].filter((c) => c.value);
};

const Fame = () => {
  const mode = useRenderMode();
  const [copied, setCopied] = useState(null);

  const credentials = useMemo(() => buildFameCredentials(), []);
  const configured = credentials.length === 3;

  const handleCopy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(key);
    setTimeout(() => setCopied(null), 1800);
  };

  // --- Phone mode: Departure Board phone variant -------------------------
  if (mode === 'phone') {
    return (
      <div className="db-page-phone">
        <PhoneTopStrip />

        <div className="db-ribbon-phone">
          <div className="title">FAME · <em>EXTERNAL</em></div>
        </div>

        <section className="db-cta-phone">
          <div className="eyebrow">FAME · HR PORTAL</div>
          <p className="desc">
            Fame can&apos;t be embedded due to provider restrictions. Use the credentials
            below if configured, then continue in the Fame portal.
          </p>

          {!configured ? (
            <div style={{ border: '1px solid var(--c-line-2)', padding: 12, fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.10em', color: 'var(--c-ink-3)', textTransform: 'uppercase' }}>
              FAME CREDENTIALS NOT CONFIGURED FOR THIS BUILD
            </div>
          ) : (
            <div className="creds">
              {credentials.map((c) => (
                <div key={c.key} className="cred">
                  <div className="grow">
                    <div className="label">{c.label}</div>
                    <div className="value">{c.value}</div>
                  </div>
                  <button type="button" className="db-pill" onClick={() => handleCopy(c.value, c.key)} aria-label={`Copy ${c.label}`}>
                    {copied === c.key ? <><Check style={{ fontSize: 12 }} /> COPIED</> : <><ContentCopy style={{ fontSize: 12 }} /> COPY</>}
                  </button>
                </div>
              ))}
            </div>
          )}

          <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="db-pill primary block">
            OPEN IN NEW TAB →
          </a>

          <div className="footnote">OPENS IN NEW TAB · TREAT CREDENTIALS AS CONFIDENTIAL</div>
        </section>
      </div>
    );
  }

  // --- Desk / wall: Departure Board ---
  return (
    <div className="db-page">
      <DepartureTopStrip />

      <div className="db-ribbon">
        <div className="db-ribbon-title">
          FAME · <em>EXTERNAL</em>
        </div>
      </div>

      <section style={{ padding: 'var(--sp-12) var(--pad-h)', maxWidth: 600 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--c-ink-3)', fontWeight: 600, marginBottom: 'var(--sp-4)' }}>
          FAME · HR PORTAL
        </div>
        <p style={{ fontFamily: "'Geist', sans-serif", fontSize: 15, color: 'var(--c-ink-2)', lineHeight: 1.6, margin: '0 0 var(--sp-6)' }}>
          Fame can&apos;t be embedded due to provider restrictions. Use the credentials
          below when configured, then continue in the Fame portal.
        </p>

        {!configured ? (
          <div style={{ border: '1px solid var(--c-line-2)', padding: 'var(--sp-4)', marginBottom: 'var(--sp-6)', fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '.10em', color: 'var(--c-ink-3)' }}>
            FAME CREDENTIALS NOT CONFIGURED FOR THIS BUILD
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 'var(--sp-6)' }}>
            {credentials.map((c) => (
              <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', border: '1px solid var(--c-line)', background: 'var(--c-bg)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--c-ink-3)', marginBottom: 2 }}>{c.label}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--c-ink)' }}>{c.value}</div>
                </div>
                <button type="button" className="db-pill" onClick={() => handleCopy(c.value, c.key)} aria-label={`Copy ${c.label}`}>
                  {copied === c.key ? 'COPIED' : 'COPY'}
                </button>
              </div>
            ))}
          </div>
        )}

        <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="db-pill primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          OPEN IN NEW TAB →
        </a>

        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.10em', color: 'var(--c-ink-4)', marginTop: 'var(--sp-4)' }}>
          OPENS IN NEW TAB · TREAT CREDENTIALS AS CONFIDENTIAL
        </p>
      </section>
    </div>
  );
};

export default Fame;
